import './styles.scss'
import { useCallback } from 'react'
import * as Toggle from '@radix-ui/react-toggle'
import { Pill } from '@Components/ticket-form/pill'
import { NumberGridType } from './number-grid-input-types'
import { Utils } from '@Util/utils'
import { useSelector } from 'react-redux'
import { RootState } from '@AppState/redux-store'
import { dynamicTemplate } from '@Util/dynamic-template'

const CLASS_NAME = 'gby-k2g-ticket-form-number-grid'

export type NumberToggleProps = {
  disabled: boolean
  pressed: boolean
  value: number
  onPressed?: (i: number, pressed: boolean) => void
  marginTop?: number
}

export type NumberGridProps = {
  className?: string
  selections: number[]
  maxSelections: number
  onSelectionChange: (selections: number[]) => void
  gridType: NumberGridType
  selectionError: boolean
}

export const NumberToggle = ({
  disabled,
  pressed,
  value,
  onPressed,
  marginTop,
}: NumberToggleProps) => {
  return (
    <Toggle.Root
      disabled={disabled}
      pressed={pressed}
      onPressedChange={(pressed) => onPressed && onPressed(value, pressed)}
      className={`${CLASS_NAME}__item`}
      value={value}
      aria-label={`Keno Number, ${value.toString()}`}
      style={marginTop ? { marginTop } : {}}>
      {Utils.zeroPadNumber(value)}
    </Toggle.Root>
  )
}

export const NumberGridInput = (props: NumberGridProps) => {
  const infoTextTemplate =
    useSelector(
      (state: RootState) => state.hostConfig.config?.ticketForm.numberInputInfoTextTemplate,
    ) ?? 'Select up to <%= maxSelections %> numbers'

  const generateInfoText = useCallback(() => {
    return dynamicTemplate(infoTextTemplate, { maxSelections: props.maxSelections })
  }, [infoTextTemplate, props.maxSelections])

  const onSelectionChange = useCallback(
    (number: number, selected: boolean) => {
      if (!selected) {
        const newSelections = props.selections.filter((val) => val != number)
        props.onSelectionChange(newSelections)
      } else {
        const newSelections = [...props.selections, number]
        props.onSelectionChange(newSelections)
      }
    },
    [props],
  )

  const createNumberToggles = () => {
    const elements = []
    for (let i = 1; i <= 80; i++) {
      const element = (
        <NumberToggle
          disabled={!props.selections.includes(i) && props.selections.length >= props.maxSelections}
          key={i}
          pressed={props.selections.includes(i)}
          onPressed={onSelectionChange}
          value={i}></NumberToggle>
      )
      elements.push(element)
    }
    return elements
  }

  return (
    <section className={CLASS_NAME}>
      <div className={`${props.className ?? CLASS_NAME}__info`}>
        {props.gridType === NumberGridType.MAIN ? (
          <label role={'heading'} aria-level={3}>
            {generateInfoText()}
          </label>
        ) : (
          <label role={'heading'} aria-level={3}>
            {`${props.maxSelections} numbers required`}
          </label>
        )}
        <Pill
          text={`${props.selections.length} Selected`}
          status={
            props.gridType === NumberGridType.MAIN && props.selectionError
              ? 'ERROR'
              : props.gridType === NumberGridType.MAIN && props.selections.length > 0
                ? 'SUCCESS'
                : props.gridType === NumberGridType.MINI_GAME &&
                    props.selections.length === props.maxSelections
                  ? 'SUCCESS'
                  : 'NEUTRAL'
          }
        />
      </div>

      <div className={`${props.className ?? CLASS_NAME}__grid`}>{createNumberToggles()}</div>
    </section>
  )
}
